import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Braces, Check, ChevronDown, Copy, CopyPlus, Database, EyeOff, Lock, Pencil, Plus, Sparkles, Trash2 } from '@/components/icons';
import { APP_URL_KEY, BUILTIN_VARIABLES, SECRET_MIN_LENGTH, TARGET_URL_HINT, VARIABLE_KEY_RE, appUrlProblem, type VariableInput } from '@1qa/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { copyText, timeAgo } from '@/lib/format';
import { placeholderFor } from '@/lib/testData';
import type { VariableView } from '@/lib/types';
import { Badge, Button, Card, CardHeader, Dialog, EmptyState, Field, Input, Textarea, Toggle, useToast } from '@/components/ui';

/** Copyable `{{KEY}}` chip showing how to use a variable in a step. */
export function PlaceholderChip({ name, secret }: { name: string; secret?: boolean }) {
  const [copied, setCopied] = useState(false);
  const text = placeholderFor(name);
  return (
    <button
      type="button"
      onClick={async () => {
        if (await copyText(text)) {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        }
      }}
      title={`Copy ${text}`}
      aria-label={`Copy ${text}`}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-field px-2 py-1 font-mono text-xs ring-1 ring-inset transition',
        secret
          ? 'bg-warning-subtle text-warning ring-warning/40 hover:ring-warning'
          : 'bg-surface-sunken text-fg ring-border hover:ring-primary',
      )}
    >
      {text}
      {copied ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5 opacity-60" />}
      <span className="sr-only" aria-live="polite">
        {copied ? 'Copied' : ''}
      </span>
    </button>
  );
}

/**
 * Explains the built-in values ({{$timestamp}} ...): a collapsed card for the Test data page's side column, under
 * the collections. Opened, the list scrolls on its own so the (sticky) column never outgrows the screen.
 */
export function BuiltinValuesHelp() {
  const builtins = Object.entries(BUILTIN_VARIABLES);
  return (
    <Card>
      <details className="group">
        <summary className="flex cursor-pointer list-none items-center gap-2 rounded-card px-4 py-3 select-none focus:outline-none focus-visible:shadow-focus [&::-webkit-details-marker]:hidden">
          <Sparkles className="size-4 text-primary" aria-hidden />
          <h2 className="flex-1 text-sm font-semibold text-fg">
            Built-in values <span className="font-normal text-fg-muted">({builtins.length})</span>
          </h2>
          <ChevronDown className="size-3.5 text-fg-muted transition group-open:rotate-180" aria-hidden />
        </summary>
        <div className="border-t border-border-subtle">
          <p className="px-4 pt-3 pb-1 text-xs leading-relaxed text-fg-muted">
            Available in every test. They get a fresh value on each run - handy for unique emails or order numbers. Click one to copy it.
          </p>
          <ul className="max-h-72 divide-y divide-border-subtle overflow-y-auto">
            {builtins.map(([name, help]) => (
              <li key={name} className="space-y-1.5 px-4 py-2.5">
                <PlaceholderChip name={name} />
                <p className="text-xs text-fg-secondary">{help}</p>
              </li>
            ))}
          </ul>
        </div>
      </details>
    </Card>
  );
}

function VariableDialog({
  open,
  onClose,
  existing,
  presetKey,
  onSubmit,
  pending,
  error,
}: {
  open: boolean;
  onClose: () => void;
  existing: VariableView | null;
  /** Name to start with when adding (e.g. a value missing from a collection). */
  presetKey?: string;
  onSubmit: (input: { key: string; value?: string; isSecret: boolean; description: string | null }) => void;
  pending: boolean;
  error: unknown;
}) {
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [isSecret, setIsSecret] = useState(false);
  const [description, setDescription] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKey(existing?.key ?? presetKey ?? '');
    setValue(existing && !existing.isSecret ? (existing.value ?? '') : '');
    setIsSecret(existing?.isSecret ?? false);
    setDescription(existing?.description ?? '');
    setShowErrors(false);
  }, [open, existing, presetKey]);

  // Editing an existing secret: an empty value keeps the stored one.
  const keepSecret = !!existing?.isSecret && isSecret && value === '';
  const keyError = !VARIABLE_KEY_RE.test(key) ? 'Use letters, digits and _ - start with a letter (max 64), e.g. ADMIN_EMAIL' : undefined;
  const isAppUrl = key === APP_URL_KEY;
  const valueError =
    isAppUrl && appUrlProblem(key, value, isSecret)
      ? isSecret
        ? 'The app address cannot be secret'
        : TARGET_URL_HINT
      : isSecret && !keepSecret && value.length < SECRET_MIN_LENGTH
      ? `Secret values need at least ${SECRET_MIN_LENGTH} characters`
      : !isSecret && existing?.isSecret && value === ''
        ? 'Enter the value - the old secret value cannot be shown'
        : undefined;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    if (keyError || valueError) return;
    onSubmit({ key, value: keepSecret ? undefined : value, isSecret, description: description.trim() || null });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={existing ? `Edit ${existing.key}` : 'Add test data'}
      description="Use it in any step by writing its name in double curly braces."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="variable-form" loading={pending}>
            {existing ? 'Save' : 'Add'}
          </Button>
        </>
      }
    >
      <form id="variable-form" onSubmit={submit} className="space-y-5" noValidate>
        <Field
          label="Name"
          error={showErrors ? keyError : undefined}
          hint={
            isAppUrl ? (
              'The app address: runs open this address instead of the project’s (handy per environment).'
            ) : key && !keyError ? (
              <>Use it in a step as <code className="font-mono">{placeholderFor(key)}</code></>
            ) : (
              'For example ADMIN_EMAIL or PRODUCT_NAME.'
            )
          }
        >
          {(id) => (
            <Input
              id={id}
              value={key}
              onChange={(e) => setKey(e.target.value.replace(/\s+/g, '_'))}
              className="font-mono"
              maxLength={64}
              spellCheck={false}
              autoComplete="off"
              invalid={showErrors && !!keyError}
            />
          )}
        </Field>

        <div className="flex items-start gap-3 rounded-card bg-surface-sunken p-3.5 ring-1 ring-border ring-inset">
          <Toggle checked={isSecret} onChange={setIsSecret} label="Secret value" />
          <div className="text-sm">
            <div className="font-medium text-fg">Secret (passwords, tokens)</div>
            <p className="text-xs text-fg-muted">
              Stored encrypted and never shown again. 1QA types it into the page without showing it to the AI, and hides it in
              logs and reports. Secrets can only be used in “Do something” steps.
            </p>
          </div>
        </div>

        <Field
          label="Value"
          error={showErrors ? valueError : undefined}
          hint={existing?.isSecret && isSecret ? '•••• saved — leave empty to keep the current value.' : undefined}
        >
          {(id) =>
            isSecret ? (
              <Input
                id={id}
                type="password"
                autoComplete="new-password"
                placeholder={existing?.isSecret ? '••••••••' : undefined}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                invalid={showErrors && !!valueError}
              />
            ) : (
              <Textarea id={id} value={value} onChange={(e) => setValue(e.target.value)} rows={3} maxLength={10_000} />
            )
          }
        </Field>

        <Field label="Description (optional)">
          {(id) => <Input id={id} value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} placeholder="What is this value for?" />}
        </Field>

        {error ? (
          <p role="alert" className="rounded-field bg-danger-subtle p-3 text-sm text-danger-text ring-1 ring-danger-line ring-inset">
            {errorMessage(error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

/** Asks for the new name of a copy (names are unique per scope). */
function CopyDialog({
  source,
  onClose,
  onSubmit,
  pending,
  error,
  target,
}: {
  source: VariableView | null;
  onClose: () => void;
  onSubmit: (key: string) => void;
  pending: boolean;
  error: unknown;
  target: string;
}) {
  const [key, setKey] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  useEffect(() => {
    if (source) {
      setKey(`${source.key}_COPY`.slice(0, 64));
      setShowErrors(false);
    }
  }, [source]);
  const keyError = !VARIABLE_KEY_RE.test(key) ? 'Use letters, digits and _ - start with a letter (max 64)' : undefined;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    if (!keyError) onSubmit(key);
  };
  return (
    <Dialog
      open={!!source}
      onClose={onClose}
      size="sm"
      title={`Copy ${source?.key ?? ''}`}
      description={`The copy goes to ${target} and belongs to you, so you can change it.${source?.isSecret ? ' The secret value is copied without being shown.' : ''}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="copy-variable-form" loading={pending}>
            Make a copy
          </Button>
        </>
      }
    >
      <form id="copy-variable-form" onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Name of the copy" error={showErrors ? keyError : undefined}>
          {(id) => (
            <Input
              id={id}
              value={key}
              onChange={(e) => setKey(e.target.value.replace(/\s+/g, '_'))}
              className="font-mono"
              maxLength={64}
              spellCheck={false}
              autoComplete="off"
              invalid={showErrors && !!keyError}
            />
          )}
        </Field>
        {error ? (
          <p role="alert" className="rounded-field bg-danger-subtle p-3 text-sm text-danger-text ring-1 ring-danger-line ring-inset">
            {errorMessage(error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

/**
 * Lists and edits variables. For a project, pass project + inherited org variables; org rows are read-only here
 * (they are edited on the organization's Test data page). Only a variable's creator (or a Project Admin)
 * edits or deletes it; anyone who can add test data here can copy any row into this scope.
 */
export function VariablesManager({
  variables,
  scope,
  orgId,
  projectId,
  canEdit,
  create,
  queryKeys,
  title,
  description,
  addRequest,
}: {
  variables: VariableView[];
  scope: 'ORG' | 'PROJECT';
  orgId: string;
  /** The project whose page this is (copies land here); omit on the organization page. */
  projectId?: string;
  canEdit: boolean;
  create: (input: VariableInput) => Promise<VariableView>;
  queryKeys: unknown[][];
  title: string;
  description: string;
  /** Opens "Add test data" with this name filled in (a new object each time). */
  addRequest?: { key: string } | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const { canModify } = useAuth();
  const [dialog, setDialog] = useState<{ open: boolean; existing: VariableView | null; presetKey?: string }>({ open: false, existing: null });
  const [deleting, setDeleting] = useState<VariableView | null>(null);
  const [copying, setCopying] = useState<VariableView | null>(null);

  const invalidate = () => queryKeys.forEach((queryKey) => qc.invalidateQueries({ queryKey }));

  const save = useMutation({
    mutationFn: (input: { key: string; value?: string; isSecret: boolean; description: string | null }) => {
      const existing = dialog.existing;
      if (existing) {
        return api.updateVariable(existing.id, {
          key: input.key,
          isSecret: input.isSecret,
          description: input.description,
          ...(input.value === undefined ? {} : { value: input.value }),
        });
      }
      return create({ key: input.key, value: input.value ?? '', isSecret: input.isSecret, description: input.description });
    },
    onSuccess: (v) => {
      toast.success(`${v.key} saved`);
      invalidate();
      setDialog({ open: false, existing: null });
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => api.deleteVariable(id),
    onSuccess: () => {
      toast.success('Test data removed');
      setDeleting(null);
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const copy = useMutation({
    mutationFn: ({ source, key }: { source: VariableView; key: string }) => api.copyVariable(source.id, { key, projectId: projectId ?? null }),
    onSuccess: (v) => {
      toast.success(`${v.key} created - it's yours to edit`);
      setCopying(null);
      invalidate();
    },
  });

  const open = (existing: VariableView | null, presetKey?: string) => {
    save.reset();
    setDialog({ open: true, existing, presetKey });
  };
  useEffect(() => {
    if (addRequest) open(null, addRequest.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only a new request opens the dialog
  }, [addRequest]);

  const rows = variables.slice().sort((a, b) => (a.scope === b.scope ? a.key.localeCompare(b.key) : a.scope === scope ? -1 : 1));
  // Organization / Project badges only help when both kinds are listed.
  const mixedScopes = scope === 'PROJECT' && rows.some((v) => v.scope === 'ORG') && rows.some((v) => v.scope === 'PROJECT');
  const writeAction = scope === 'ORG' ? 'variables.org.manage' : 'test.write';
  const editable = (v: VariableView) => canEdit && v.scope === scope && canModify(writeAction, v.createdBy?.id, orgId);

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Database className="size-4 text-primary" /> {title}
          </span>
        }
        description={description}
        actions={
          canEdit && (
            <Button size="sm" onClick={() => open(null)} icon={<Plus className="size-4" />}>
              Add test data
            </Button>
          )
        }
      />
      {rows.length === 0 ? (
        <div className="p-5">
          <EmptyState
            icon={<Braces className="size-6" />}
            title="No test data yet"
            description="Save values you use in many tests - like a login email or a product name - once, then write {{NAME}} in any step."
            action={
              canEdit && (
                <Button onClick={() => open(null)} icon={<Plus className="size-4" />}>
                  Add test data
                </Button>
              )
            }
          />
        </div>
      ) : (
        <ul className="divide-y divide-border-subtle">
          {rows.map((v) => (
            <li key={v.id} className={clsx('group flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5', v.overridden && 'opacity-60')}>
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <PlaceholderChip name={v.key} secret={v.isSecret} />
                {v.key === APP_URL_KEY && <Badge tone="brand">App address</Badge>}
                {mixedScopes && <Badge tone={v.scope === 'ORG' ? 'info' : 'neutral'}>{v.scope === 'ORG' ? 'Organization' : 'Project'}</Badge>}
                {v.isSecret && (
                  <Badge tone="warning" icon={<Lock className="size-3" />}>
                    Secret
                  </Badge>
                )}
                {v.overridden && (
                  <span title="A project value with the same name is used instead.">
                    <Badge>Overridden by project</Badge>
                  </span>
                )}
              </div>
              <div className="w-full min-w-0 text-sm sm:w-auto sm:max-w-[40%] sm:flex-1">
                {v.isSecret ? (
                  <span className="inline-flex items-center gap-1.5 font-mono text-fg-muted">
                    <EyeOff className="size-3.5" /> ••••••
                  </span>
                ) : (
                  <span className="block truncate font-mono text-fg" title={v.value ?? ''}>
                    {v.value || <span className="text-fg-muted italic">empty</span>}
                  </span>
                )}
                {v.description && <span className="block truncate text-xs text-fg-muted">{v.description}</span>}
              </div>
              <span className="hidden text-xs text-fg-muted md:block">
                {v.createdBy ? `by ${v.createdBy.name} · ` : ''}
                {timeAgo(v.updatedAt)}
              </span>
              <div className="flex gap-1">
                {canEdit && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      copy.reset();
                      setCopying(v);
                    }}
                    icon={<CopyPlus className="size-4" />}
                    aria-label={`Copy ${v.key}`}
                    title="Make a copy you can edit"
                  />
                )}
                {editable(v) && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => open(v)} icon={<Pencil className="size-4" />} aria-label={`Edit ${v.key}`} />
                    <Button size="sm" variant="ghost" onClick={() => setDeleting(v)} icon={<Trash2 className="size-4 text-danger-text" />} aria-label={`Delete ${v.key}`} />
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <VariableDialog
        open={dialog.open}
        existing={dialog.existing}
        presetKey={dialog.presetKey}
        onClose={() => setDialog({ open: false, existing: null })}
        onSubmit={(input) => save.mutate(input)}
        pending={save.isPending}
        error={save.error}
      />

      <CopyDialog
        source={copying}
        onClose={() => setCopying(null)}
        onSubmit={(key) => copying && copy.mutate({ source: copying, key })}
        pending={copy.isPending}
        error={copy.error}
        target={scope === 'PROJECT' ? 'this project' : 'the organization'}
      />

      <Dialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        size="sm"
        title={`Delete ${deleting?.key ?? ''}?`}
        description="Tests that still use it will stop with an error until you add it again or change those steps."
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button variant="danger" loading={del.isPending} onClick={() => deleting && del.mutate(deleting.id)}>
              Delete
            </Button>
          </>
        }
      >
        <span className="sr-only">Confirm deletion</span>
      </Dialog>
    </Card>
  );
}
